import React, { useState, useEffect } from 'react';
import {
  ChevronDown, Save, AlertTriangle, Users, Calendar, IndianRupee,
  FileText, MapPin, Shield, Plus, X, Trash2
} from 'lucide-react';
import { wagesAPI, establishmentAPI, employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const LossDamages = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [damages, setDamages] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');

  const [formData, setFormData] = useState({
    establishment: lockedEstId || '',
    employee: '',
    damageType: 'equipment-damage',
    otherDamageType: '',
    amount: '',
    incidentDate: new Date().toISOString().split('T')[0],
    deductionDate: '',
    description: '',
    location: '',
    severity: 'minor',
    recoveryMethod: 'salary-deduction',
    installments: '1',
    witnessName: '',
    reportedBy: '',
    approvedBy: '',
    status: 'pending'
  });

  const damageTypes = [
    { value: 'equipment-damage', label: 'Equipment Damage' },
    { value: 'property-damage', label: 'Property Damage' },
    { value: 'vehicle-damage', label: 'Vehicle Damage' },
    { value: 'tool-damage', label: 'Tool/Machinery Damage' },
    { value: 'inventory-loss', label: 'Inventory Loss' },
    { value: 'cash-shortage', label: 'Cash Shortage' },
    { value: 'material-wastage', label: 'Material Wastage' },
    { value: 'other', label: 'Other' }
  ];

  const severityLevels = [
    { value: 'minor', label: 'Minor' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'major', label: 'Major' },
    { value: 'critical', label: 'Critical' }
  ];

  const recoveryMethods = [
    { value: 'salary-deduction', label: 'Salary Deduction' },
    { value: 'installments', label: 'Installments' },
    { value: 'lumpsum', label: 'Lump Sum Payment' },
    { value: 'insurance-claim', label: 'Insurance Claim' }
  ];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) {
      setSelectedEstablishment(lockedEstId);
      setFormData(prev => ({ ...prev, establishment: lockedEstId }));
      fetchEmployees(lockedEstId);
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) {
      fetchEmployees(lockedEstId);
      setLoading(false);
    } else {
      fetchEstablishments();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEstablishment) {
      fetchDamages();
    }
  }, [selectedEstablishment]);

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      const estabs = response.establishments || [];
      setEstablishments(estabs);
      if (estabs.length > 0) {
        setSelectedEstablishment(estabs[0].id.toString());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setLoading(false);
    }
  };

  const fetchDamages = async () => {
    try {
      setLoading(true);
      const response = await wagesAPI.getDamages({ establishment_id: selectedEstablishment });
      setDamages(response.damages || []);
    } catch (error) {
      console.error('Error fetching damages:', error);
      setDamages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async (establishmentId) => {
    try {
      const response = await employeeAPI.getByEstablishment(establishmentId);
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const handleEstablishmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      establishment: value,
      employee: ''
    }));
    setEmployees([]);
    if (value) {
      fetchEmployees(value);
    }
  };

  const handleSubmit = async () => {
    if (!formData.establishment || !formData.employee || !formData.damageType || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.damageType === 'other' && !formData.otherDamageType.trim()) {
      alert('Please specify the damage type');
      return;
    }

    try {
      const damageData = {
        establishmentId: parseInt(formData.establishment),
        employeeId: formData.employee,
        damageType: formData.damageType === 'other' ? formData.otherDamageType.trim() : formData.damageType,
        amount: parseFloat(formData.amount),
        incidentDate: formData.incidentDate,
        deductionDate: formData.deductionDate,
        description: formData.description,
        location: formData.location,
        severity: formData.severity,
        recoveryMethod: formData.recoveryMethod,
        installments: parseInt(formData.installments) || 1,
        witnessName: formData.witnessName,
        reportedBy: formData.reportedBy,
        approvedBy: formData.approvedBy,
        status: formData.status
      };

      await wagesAPI.addDamage(damageData);
      notifySavedAndReload('\u2713 Damage/Loss record saved successfully!');
      resetForm();
      fetchDamages();
    } catch (error) {
      console.error('Error saving damage/loss:', error);
      alert('\u274c Error saving damage/loss: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this damage/loss record?')) return;

    try {
      await wagesAPI.deleteDamage(id);
      notifySavedAndReload('Damage/Loss record archived successfully!');
      fetchDamages();
    } catch (error) {
      console.error('Error archiving damage/loss:', error);
      alert('Error archiving damage/loss');
    }
  };

  const resetForm = () => {
    setFormData({
      establishment: lockedEstId || '',
      employee: '',
      damageType: 'equipment-damage',
      otherDamageType: '',
      amount: '',
      incidentDate: new Date().toISOString().split('T')[0],
      deductionDate: '',
      description: '',
      location: '',
      severity: 'minor',
      recoveryMethod: 'salary-deduction',
      installments: '1',
      witnessName: '',
      reportedBy: '',
      approvedBy: '',
      status: 'pending'
    });
    setEmployees([]);
    setShowModal(false);
  };

  const damageTypeLabel = (t) => {
    const found = damageTypes.find(dt => dt.value === t);
    return found ? found.label : t;
  };

  const severityStyle = (s) => {
    const map = {
      minor:    { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'Minor' },
      moderate: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: 'Moderate' },
      major:    { bg: '#ffedd5', color: '#9a3412', border: '#fed7aa', label: 'Major' },
      critical: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', label: 'Critical' },
    };
    const st = map[s] || map.minor;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.2rem 0.6rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: '600',
      background: st.bg, color: st.color, border: `1px solid ${st.border}`,
    };
  };

  const statusStyle = (s) => {
    const map = {
      pending:  { bg: '#fffbeb', color: '#b45309', border: '#fde68a', label: 'Pending' },
      resolved: { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'Resolved' },
      disputed: { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', label: 'Disputed' },
      waived:   { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd', label: 'Waived' },
    };
    const st = map[s] || map.pending;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.25rem 0.7rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: '600',
      background: st.bg, color: st.color, border: `1px solid ${st.border}`,
    };
  };

  // Shared input style
  const inputBase = {
    width: '100%', padding: '0.65rem 0.85rem',
    border: '1.5px solid var(--theme-border, #d1d5db)', borderRadius: '0.5rem',
    fontSize: '0.875rem', background: 'var(--theme-surface, #fdf9f3)',
    color: 'var(--theme-text, #111827)', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };
  const labelBase = {
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    fontSize: '0.8rem', fontWeight: '600',
    color: 'var(--theme-text, #374151)', marginBottom: '0.4rem',
  };

  return (
    <div style={{ maxWidth: '1100px' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '0.65rem',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 8px rgba(239,68,68,0.35)',
          }}>
            <AlertTriangle size={22} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--theme-text, #111827)', margin: 0 }}>
              Loss & Damages
            </h2>
            <p style={{ color: 'var(--theme-textSecondary, #6b7280)', fontSize: '0.8rem', margin: 0 }}>
              Manage employee damage and loss records
            </p>
          </div>
        </div>
        {selectedEstablishment && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.55rem 1.1rem',
              background: 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
              color: '#fff', border: 'none', borderRadius: '0.5rem',
              fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
              boxShadow: '0 3px 8px rgba(99,102,241,0.3)',
              transition: 'transform 0.1s, box-shadow 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 14px rgba(99,102,241,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(99,102,241,0.3)'; }}
          >
            <Plus size={16} /> Add Damage
          </button>
        )}
      </div>

      {/* Establishment Selector */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
        />
      )}

      {/* Summary Cards */}
      {!loading && damages.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem', marginBottom: '1.25rem',
        }}>
          {[
            {
              label: 'Total Damages',
              value: `\u20b9${damages.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0).toLocaleString('en-IN')}`,
              color: '#e11d48', bg: '#fff1f2', border: '#fecdd3',
            },
            {
              label: 'Pending',
              value: damages.filter(d => d.status === 'pending').length,
              color: '#d97706', bg: '#fffbeb', border: '#fde68a',
            },
            {
              label: 'Total Records',
              value: damages.length,
              color: '#475569', bg: '#f8fafc', border: '#cbd5e1',
            },
          ].map(c => (
            <div key={c.label} style={{
              background: c.bg, border: `1px solid ${c.border}`, borderRadius: '0.65rem',
              padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.15rem',
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '600', color: c.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {c.label}
              </span>
              <span style={{ fontSize: '1.3rem', fontWeight: '800', color: c.color }}>
                {c.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem',
        }}
        onClick={() => resetForm()}>
          <div style={{
            background: 'var(--theme-surface, #fdf9f3)',
            borderRadius: '1rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--theme-border, #e5e7eb)',
              background: 'linear-gradient(135deg, #fef2f2, #fecaca)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 6px rgba(220,38,38,0.3)',
                }}>
                  <AlertTriangle size={22} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--theme-text, #1f2937)', margin: 0 }}>
                    Add Damage/Loss Record
                  </h3>
                  <p style={{ fontSize: '0.78rem', color: 'var(--theme-textSecondary, #6b7280)', margin: '0.15rem 0 0' }}>
                    Report damage or loss incident
                  </p>
                </div>
              </div>
              <button onClick={() => resetForm()} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.4rem', border: 'none', background: 'rgba(0,0,0,0.06)',
                borderRadius: '0.4rem', cursor: 'pointer', color: '#6b7280',
              }}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>

                {/* Organization */}
                {!lockedEstId && (
                  <div>
                    <label style={labelBase}>Organization <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={formData.establishment} onChange={(e) => handleEstablishmentChange(e.target.value)}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Organization</option>
                      {establishments.map(est => <option key={est.id} value={est.id}>{est.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Employee */}
                {(formData.establishment || lockedEstId) && (
                  <div>
                    <label style={labelBase}>Employee <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={formData.employee}
                      onChange={(e) => setFormData(prev => ({ ...prev, employee: e.target.value }))}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Damage Details */}
                {formData.employee && (
                  <>
                    <div style={{ height: '1px', background: 'var(--theme-border, #e5e7eb)' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                      {/* Damage Type */}
                      <div>
                        <label style={labelBase}>Damage Type <span style={{ color: '#dc2626' }}>*</span></label>
                        <select value={formData.damageType}
                          onChange={(e) => setFormData(prev => ({ ...prev, damageType: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          {damageTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                      </div>

                      {/* Other Damage Type */}
                      {formData.damageType === 'other' && (
                        <div>
                          <label style={labelBase}>Specify Type <span style={{ color: '#dc2626' }}>*</span></label>
                          <input type="text" value={formData.otherDamageType}
                            onChange={(e) => setFormData(prev => ({ ...prev, otherDamageType: e.target.value }))}
                            placeholder="Enter damage type" style={inputBase} />
                        </div>
                      )}

                      {/* Amount */}
                      <div>
                        <label style={labelBase}><IndianRupee size={13} /> Amount (Rs) <span style={{ color: '#dc2626' }}>*</span></label>
                        <input type="number" value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          placeholder="0.00" min="0" step="0.01" style={inputBase} />
                      </div>

                      {/* Severity */}
                      <div>
                        <label style={labelBase}><Shield size={13} /> Severity</label>
                        <select value={formData.severity}
                          onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          {severityLevels.map(level => <option key={level.value} value={level.value}>{level.label}</option>)}
                        </select>
                      </div>

                      {/* Incident Date */}
                      <div>
                        <label style={labelBase}><Calendar size={13} /> Incident Date</label>
                        <DateInput value={formData.incidentDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, incidentDate: e.target.value }))}
                          style={inputBase} />
                      </div>

                      {/* Deduction Date */}
                      <div>
                        <label style={labelBase}><Calendar size={13} /> Deduction Date</label>
                        <DateInput value={formData.deductionDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, deductionDate: e.target.value }))}
                          style={inputBase} />
                      </div>

                      {/* Recovery Method */}
                      <div>
                        <label style={labelBase}>Recovery Method</label>
                        <select value={formData.recoveryMethod}
                          onChange={(e) => setFormData(prev => ({ ...prev, recoveryMethod: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          {recoveryMethods.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}
                        </select>
                      </div>

                      {/* Location */}
                      <div>
                        <label style={labelBase}><MapPin size={13} /> Location</label>
                        <input type="text" value={formData.location}
                          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="Enter location" style={inputBase} />
                      </div>

                      {/* Description */}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={labelBase}><FileText size={13} /> Description</label>
                        <textarea value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3} placeholder="Describe the incident"
                          style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '0.65rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--theme-border, #e5e7eb)',
              background: 'var(--theme-surfaceHover, #f0ebe0)',
            }}>
              <button onClick={resetForm} style={{
                padding: '0.55rem 1.1rem', border: '1px solid var(--theme-border, #d1d5db)',
                background: 'var(--theme-surface, #fdf9f3)', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600', color: 'var(--theme-textSecondary, #374151)',
                cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={!formData.employee} style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.55rem 1.35rem',
                background: !formData.employee
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
                color: '#fff', border: 'none', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600',
                cursor: !formData.employee ? 'not-allowed' : 'pointer',
                boxShadow: !formData.employee ? 'none' : '0 3px 8px rgba(99,102,241,0.3)',
              }}>
                <Save size={16} /> Save Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {selectedEstablishment && (
        <div style={{
          background: 'var(--theme-surface, #fdf9f3)',
          borderRadius: '0.75rem',
          border: '1px solid var(--theme-border, #e5e7eb)',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{
                width: '36px', height: '36px', margin: '0 auto 0.75rem',
                border: '3px solid var(--theme-border, #e5e7eb)',
                borderTopColor: '#ef4444',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: 'var(--theme-textSecondary)', fontSize: '0.85rem', margin: 0 }}>Loading damages...</p>
            </div>
          ) : damages.length === 0 ? (
            <div style={{ padding: '3.5rem 2rem', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', margin: '0 auto 1rem', borderRadius: '50%',
                background: 'linear-gradient(135deg, #fecdd3, #fda4af)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AlertTriangle size={28} style={{ color: '#be123c' }} />
              </div>
              <p style={{ color: 'var(--theme-text, #374151)', fontSize: '0.95rem', fontWeight: '600', margin: '0 0 0.25rem' }}>
                No damage or loss records yet
              </p>
              <p style={{ color: 'var(--theme-textSecondary, #9ca3af)', fontSize: '0.82rem', margin: 0 }}>
                Click "Add Damage" to create the first record.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, var(--theme-surfaceHover, #f0ebe0), var(--theme-surface, #fdf9f3))',
                    borderBottom: '2px solid var(--theme-border, #e5e7eb)',
                  }}>
                    {['Employee', 'Type', 'Amount', 'Severity', 'Status', 'Incident Date', 'Actions'].map(h => (
                      <th key={h} style={{
                        textAlign: h === 'Amount' ? 'right' : h === 'Severity' || h === 'Status' || h === 'Actions' ? 'center' : 'left',
                        padding: '0.75rem 1rem', fontSize: '0.72rem', fontWeight: '700',
                        color: 'var(--theme-textSecondary, #6b7280)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {damages.map((damage, i) => (
                    <tr key={damage.id} style={{
                      borderBottom: '1px solid var(--theme-border, #f0f0f0)',
                      background: i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--theme-accent, #eef2ff)'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)'}
                    >
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--theme-text, #111827)' }}>
                          {damage.employee_name || damage.employee_id}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.2rem 0.6rem', borderRadius: '0.35rem',
                          background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
                          fontSize: '0.78rem', fontWeight: '600',
                        }}>
                          {damageTypeLabel(damage.damage_type)}
                        </span>
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', textAlign: 'right',
                        fontSize: '0.9rem', fontWeight: '700', color: '#b91c1c',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {'\u20b9'}{parseFloat(damage.amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'center' }}>
                        <span style={severityStyle(damage.severity)}>
                          {damage.severity ? damage.severity.charAt(0).toUpperCase() + damage.severity.slice(1) : 'Minor'}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'center' }}>
                        <span style={statusStyle(damage.status)}>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            background: 'currentColor', display: 'inline-block',
                          }} />
                          {damage.status ? damage.status.charAt(0).toUpperCase() + damage.status.slice(1) : 'Pending'}
                        </span>
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', fontSize: '0.84rem',
                        color: 'var(--theme-textSecondary, #6b7280)',
                      }}>
                        {damage.incident_date || '-'}
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'center' }}>
                        <button onClick={() => handleDelete(damage.id)} title="Archive" style={{
                          padding: '0.35rem', background: '#fef2f2', border: '1px solid #fecaca',
                          borderRadius: '0.4rem', cursor: 'pointer', color: '#dc2626',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LossDamages;
